import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useClientes } from "@/hooks/useClientes";
import {
  useClienteAccesses,
  useInviteClienteUser,
  useRevokeClienteAccess,
} from "@/hooks/useClienteAccesses";
import { PlanGate } from "@/components/PlanGate";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { AppShell } from "@/components/layout/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Building2,
  Users,
  UserPlus,
  Mail,
  Trash2,
  Loader2,
  ShieldCheck,
  Search,
} from "lucide-react";
import { toast } from "sonner";

interface ClienteRow {
  id: string;
  nombre: string;
  nit: string | null;
  pais: string | null;
  contacto: string | null;
  email: string | null;
}

export default function AdminClientes() {
  const { isAuthenticated, loading } = useAuth();
  const [selectedCliente, setSelectedCliente] = useState<ClienteRow | null>(null);
  const [search, setSearch] = useState("");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Cargando...</p>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <AppShell>
      <header className="h-14 border-b border-border bg-card/40 backdrop-blur flex items-center px-4 gap-3 sticky top-0 z-30">
        <SidebarTrigger />
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          <h1 className="text-base font-semibold text-foreground truncate">
            Gestión de Clientes & Portal
          </h1>
        </div>
      </header>

      <main className="flex-1">
        <PlanGate
          allowedPlans={["PRO", "ENTERPRISE"]}
          featureName="el Portal de Clientes B2B"
          description="Invita a tus clientes a una vista premium de solo lectura para que sigan sus embarques y costos. Disponible en planes Pro y Enterprise."
        >
          <ClientesContent
            search={search}
            onSearch={setSearch}
            selected={selectedCliente}
            onSelect={setSelectedCliente}
          />
        </PlanGate>
      </main>
    </AppShell>
  );
}

/* ─────────────── Content (gated) ─────────────── */

function ClientesContent({
  search,
  onSearch,
  selected,
  onSelect,
}: {
  search: string;
  onSearch: (v: string) => void;
  selected: ClienteRow | null;
  onSelect: (c: ClienteRow | null) => void;
}) {
  const { data: clientes = [], isLoading } = useClientes();

  const filtered = (clientes as ClienteRow[]).filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      c.nombre?.toLowerCase().includes(q) ||
      c.nit?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header / Intro */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Clientes con acceso al Portal
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Administra los usuarios externos (importadores) que pueden ver sus embarques en modo solo lectura.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar cliente por nombre, NIT o correo…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Table */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {isLoading ? "Cargando…" : `${filtered.length} clientes`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No hay clientes registrados todavía.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>NIT</TableHead>
                  <TableHead>País</TableHead>
                  <TableHead className="text-right">Acceso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer">
                    <TableCell className="font-medium text-foreground">{c.nombre}</TableCell>
                    <TableCell className="text-muted-foreground">{c.nit ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{c.pais ?? "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => onSelect(c)} className="gap-2">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Gestionar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Access management sheet */}
      <AccessSheet cliente={selected} onClose={() => onSelect(null)} />
    </div>
  );
}

/* ─────────────── Access Sheet ─────────────── */

function AccessSheet({
  cliente,
  onClose,
}: {
  cliente: ClienteRow | null;
  onClose: () => void;
}) {
  const open = !!cliente;
  const { data: accesses = [], isLoading } = useClienteAccesses(cliente?.id ?? null);
  const invite = useInviteClienteUser();
  const revoke = useRevokeClienteAccess();
  const [email, setEmail] = useState("");

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleInvite = async () => {
    if (!cliente || !isValidEmail) return;
    try {
      await invite.mutateAsync({ email: email.trim(), cliente_id: cliente.id });
      toast.success(`Invitación enviada a ${email.trim()}`);
      setEmail("");
    } catch (err: any) {
      toast.error(err?.message || "Error al enviar invitación");
    }
  };

  const handleRevoke = async (id: string) => {
    if (!cliente) return;
    try {
      await revoke.mutateAsync({ id, cliente_id: cliente.id });
      toast.success("Acceso revocado");
    } catch (err: any) {
      toast.error(err?.message || "Error al revocar");
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            {cliente?.nombre}
          </SheetTitle>
          <SheetDescription>
            Gestiona los usuarios que pueden acceder al portal de este cliente en modo solo lectura.
          </SheetDescription>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Invite form */}
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-primary" />
                Invitar nuevo usuario
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="invite-cliente-email">Correo electrónico</Label>
                <Input
                  id="invite-cliente-email"
                  type="email"
                  placeholder="cfo@cliente.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={invite.isPending}
                />
              </div>
              <Button
                onClick={handleInvite}
                disabled={!isValidEmail || invite.isPending}
                className="w-full gap-2"
              >
                {invite.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                Enviar invitación
              </Button>
            </CardContent>
          </Card>

          {/* Existing accesses */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              Usuarios con acceso
              {!isLoading && (
                <Badge variant="outline" className="text-[10px] ml-1">
                  {accesses.length}
                </Badge>
              )}
            </h3>

            {isLoading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : accesses.length === 0 ? (
              <div className="text-center text-xs text-muted-foreground border border-dashed border-border rounded-lg py-6">
                Aún no hay usuarios con acceso al portal.
              </div>
            ) : (
              <div className="space-y-2">
                {accesses.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/20 hover:bg-muted/30 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{a.email}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {a.rol} · desde {new Date(a.created_at).toLocaleDateString("es-CO")}
                      </p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Revocar acceso?</AlertDialogTitle>
                          <AlertDialogDescription>
                            <strong>{a.email}</strong> ya no podrá ver los embarques de{" "}
                            <strong>{cliente?.nombre}</strong>. Esta acción no elimina la cuenta del usuario.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleRevoke(a.id)}>
                            Revocar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
